import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import DashboardService from '../services/dashboardService';
import Icons from './Icons';

// Turn an announcement record into the same shape the notification list uses
const announcementToNotification = (a) => ({
  id: `announcement-${a.id}`,
  announcementId: a.id,
  type: 'announcement',
  priority: a.priority,
  is_pinned: a.is_pinned,
  title: a.title,
  message: a.content,
  is_read: a.is_read,
  created_at: a.created_at,
  created_by_first_name: a.author_first_name,
  created_by_last_name: a.author_last_name,
});

// Shared notification feed, rendered inside whichever page shell (volunteer or admin) hosts it.
// Announcements are only merged in for volunteers — admins manage announcements from their own
// dedicated Announcements tab, so mixing them back into Notifications would defeat the point of
// keeping the two separate.
const NotificationsList = ({ isAdmin = false, includeAnnouncements = !isAdmin }) => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all', 'unread', 'read'
  const [typeFilter, setTypeFilter] = useState('all'); // 'all', 'task', 'event', 'document', 'announcement'

  useEffect(() => {
    fetchNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const [notificationsResult, announcementsResult] = await Promise.allSettled([
        api.get('/notifications'),
        includeAnnouncements ? DashboardService.getAnnouncements() : Promise.resolve(null)
      ]);

      const baseNotifications = notificationsResult.status === 'fulfilled' && notificationsResult.value.data.success
        ? notificationsResult.value.data.notifications
        : [];

      const announcementNotifications = includeAnnouncements && announcementsResult.status === 'fulfilled' && announcementsResult.value
        ? (announcementsResult.value.announcements || []).map(announcementToNotification)
        : [];

      setNotifications(
        [...baseNotifications, ...announcementNotifications].sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        )
      );
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  // Mark notification as read and navigate
  const handleNotificationClick = async (notification) => {
    try {
      if (!notification.is_read) {
        if (notification.type === 'announcement') {
          await DashboardService.markAnnouncementRead(notification.announcementId);
        } else {
          await api.put(`/notifications/${notification.id}/read`);
        }
        // Update local state
        setNotifications(prev =>
          prev.map(n => n.id === notification.id ? { ...n, is_read: true } : n)
        );
      }

      // Navigate to the item (announcements show their full content inline, so no navigation)
      const path = getNavigationPath(notification);
      if (path) {
        navigate(path);
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Mark all as read
  const handleMarkAllRead = async () => {
    try {
      const unreadAnnouncementIds = notifications
        .filter(n => n.type === 'announcement' && !n.is_read)
        .map(n => n.announcementId);

      await Promise.allSettled([
        api.put('/notifications/read-all'),
        ...unreadAnnouncementIds.map(id => DashboardService.markAnnouncementRead(id))
      ]);

      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  // Get navigation path based on notification type and role
  const getNavigationPath = (notification) => {
    if (isAdmin) {
      switch (notification.type) {
        case 'task':
          return `/admin-dashboard/tasks`;
        case 'event':
          return `/admin-dashboard/events`;
        case 'document':
          return `/admin-dashboard/documents`;
        default:
          return null;
      }
    }
    switch (notification.type) {
      case 'task':
        return `/my-tasks`;
      case 'event':
        return `/calendar`;
      case 'document':
        return `/documents`;
      default:
        return null;
    }
  };

  // Filter notifications by read status and type independently
  const filteredNotifications = notifications.filter(n => {
    const statusMatch = filter === 'unread' ? !n.is_read : filter === 'read' ? n.is_read : true;
    const typeMatch = typeFilter === 'all' || n.type === typeFilter;
    return statusMatch && typeMatch;
  });

  const typeCounts = {
    all: notifications.length,
    task: notifications.filter(n => n.type === 'task').length,
    event: notifications.filter(n => n.type === 'event').length,
    document: notifications.filter(n => n.type === 'document').length,
    announcement: notifications.filter(n => n.type === 'announcement').length,
  };

  // Group notifications by date
  const groupByDate = (notifications) => {
    const groups = {};
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    notifications.forEach(notification => {
      const notificationDate = new Date(notification.created_at);
      const notificationDay = new Date(notificationDate.getFullYear(), notificationDate.getMonth(), notificationDate.getDate());

      let label;
      if (notificationDay.getTime() === today.getTime()) {
        label = 'Today';
      } else if (notificationDay.getTime() === yesterday.getTime()) {
        label = 'Yesterday';
      } else {
        label = notificationDate.toLocaleDateString('en-US', {
          weekday: 'long',
          month: 'short',
          day: 'numeric'
        });
      }

      if (!groups[label]) {
        groups[label] = [];
      }
      groups[label].push(notification);
    });

    return groups;
  };

  const groupedNotifications = groupByDate(filteredNotifications);

  // Get icon for notification type — matches the icons used for these sections elsewhere in the dashboard
  const getTypeIcon = (notification) => {
    switch (notification.type) {
      case 'event':
        return { icon: Icons.calendar('w-5 h-5'), color: 'text-purple-600', bg: 'bg-purple-100' };
      case 'task':
        return { icon: Icons.clipboard('w-5 h-5'), color: 'text-blue-600', bg: 'bg-blue-100' };
      case 'document':
        return { icon: Icons.doc('w-5 h-5'), color: 'text-green-600', bg: 'bg-green-100' };
      case 'announcement':
        if (notification.priority === 'urgent') {
          return { icon: Icons.alertTriangle('w-5 h-5'), color: 'text-danger-600', bg: 'bg-danger-100' };
        }
        if (notification.priority === 'important') {
          return { icon: Icons.bolt('w-5 h-5'), color: 'text-warning-600', bg: 'bg-warning-100' };
        }
        return { icon: Icons.megaphone('w-5 h-5'), color: 'text-primary-600', bg: 'bg-primary-100' };
      default:
        return { icon: Icons.bell('w-5 h-5'), color: 'text-gray-600', bg: 'bg-gray-100' };
    }
  };

  // Format time
  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading notifications...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Notifications</h1>
          <p className="text-sm text-gray-500 mt-1">
            {unreadCount > 0 ? (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {unreadCount} unread
              </span>
            ) : (
              'All caught up!'
            )}
          </p>
        </div>
        {unreadCount > 0 && (
          <button
            onClick={handleMarkAllRead}
            className="text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            Mark all as read
          </button>
        )}
      </div>

      {/* Filter Tabs */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="flex border-b border-gray-200">
          {['all', 'unread', 'read'].map((tab) => (
            <button
              key={tab}
              onClick={() => setFilter(tab)}
              className={`flex-1 px-4 py-3 text-sm font-medium capitalize ${
                filter === tab
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Type filter — independent of read/unread status above */}
        <div className="flex items-center flex-wrap gap-2 px-4 py-3">
          {[
            { id: 'all', label: 'All Types' },
            { id: 'task', label: 'Tasks' },
            { id: 'event', label: 'Events' },
            { id: 'document', label: 'Documents' },
            ...(includeAnnouncements ? [{ id: 'announcement', label: 'Announcements' }] : []),
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTypeFilter(t.id)}
              className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                typeFilter === t.id
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {t.label}
              <span className={`ml-1.5 ${typeFilter === t.id ? 'text-primary-200' : 'text-gray-400'}`}>
                {typeCounts[t.id]}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Notifications List */}
      {filteredNotifications.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <svg className="w-12 h-12 text-gray-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {filter === 'unread' ? 'No unread notifications' : 'No notifications'}
          </h3>
          <p className="text-gray-600">
            {filter === 'unread'
              ? "You're all caught up!"
              : 'Notifications will appear here when you receive them.'}
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedNotifications).map(([dateLabel, dateNotifications]) => (
            <div key={dateLabel}>
              <h2 className="text-sm font-semibold text-gray-500 mb-3">{dateLabel}</h2>
              <div className="bg-white rounded-lg shadow divide-y divide-gray-200">
                {dateNotifications.map((notification) => {
                  const { icon, color, bg } = getTypeIcon(notification);
                  return (
                    <div
                      key={notification.id}
                      onClick={() => handleNotificationClick(notification)}
                      className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                        !notification.is_read ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        <div className={`relative flex-shrink-0 w-10 h-10 rounded-full ${bg} ${color} flex items-center justify-center`}>
                          {icon}
                          {!notification.is_read && (
                            <span className="absolute -top-1 -right-1 w-3 h-3 bg-primary-500 rounded-full border-2 border-white"></span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1">
                              <div className="flex items-center flex-wrap gap-2">
                                <h3 className={`text-base ${
                                  !notification.is_read ? 'font-semibold text-gray-900' : 'font-medium text-gray-700'
                                }`}>
                                  {notification.title}
                                </h3>
                                {notification.is_pinned && (
                                  <span className="flex items-center gap-1 text-xs text-gray-400">
                                    {Icons.pin('w-3 h-3')} Pinned
                                  </span>
                                )}
                              </div>
                              {notification.message && (
                                <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                                  {notification.message}
                                </p>
                              )}
                              <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                                <span>{formatTime(notification.created_at)}</span>
                                {notification.created_by_first_name && (
                                  <>
                                    <span>•</span>
                                    <span>by {notification.created_by_first_name} {notification.created_by_last_name}</span>
                                  </>
                                )}
                              </div>
                            </div>
                            {notification.type !== 'announcement' && (
                              <span className="text-xs text-blue-600 hover:text-blue-700 whitespace-nowrap">
                                View →
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default NotificationsList;
